<script module lang="ts">
	export enum CellStyle {
		DEFAULT,
		STRIPED
	}

	export enum rowActions {
		EDIT = 'edit',
		DELETE = 'delete',
		DOWNLOAD = 'download',
		UPLOAD = 'upload',
		COPY = 'copy',
		MORE = 'more',
		DUPLICATE = 'duplicate',
		NAVIGATE = 'navigate'
	}
	export enum SortOrder {
		ASC = 'asc',
		DESC = 'desc'
	}
</script>

<script lang="ts">
	import TableHeader from './TableHeader.svelte'
	import ContextMenuRenderer from './ContextMenuRenderer.svelte'
	import type { ContextMenuProps, ContextMenuItem } from '@repo/commons/models'
	import TableBody from './TableBody.svelte'
	import ProjectConfiguration from './ProjectConfiguration.svelte'

	interface Props {
		toastsStore?: any
		rows?: any[]
		columns?: string[]
		columnsToSplit?: Record<string, string>
		jsonColumns?: string[]
		filteredOutColumns?: string[]
		locale?: string
		showSelectionCheckboxes?: boolean
		showTableTopDeleteButton?: boolean
		showTableTopNewProjectButton?: boolean
		shouldHighlightCurlyBraces?: boolean
		colorRowOnHover?: boolean
		selectedRowsSet?: Set<any>
		showFilters?: boolean
		enableSorting?: boolean
		columnStyles?: Record<string, string>
		columnComponents?: Record<string, any>
		editableColumns?: string[]
		columnWidths?: string[] // optional override
		onSelectedRowsSetUpdate?: ((updated: Set<any>) => void) | null
		contextMenuOptions?: ((row: any, index: number) => ContextMenuItem[]) | null
		onDoubleClickRow?: ((row: any) => void) | null
		onsubmit?: ((row: Record<string, string>) => void) | null
		onRowClicked: ((row: any) => void) | null
		onDeleteButtonClicked?: ((rowIdsToDelete: Set<any>) => void) | null
		onNewProjectButtonClicked?: (() => void) | null
		onActionHandler: ((action: rowActions, id: string) => void) | null
	}

	let {
		toastsStore = [],
		rows = [],
		columns = $bindable([]),
		columnsToSplit = {},
		jsonColumns = [],
		filteredOutColumns = [],
		locale = 'de',
		showSelectionCheckboxes = true,
		showTableTopDeleteButton = true,
		showTableTopNewProjectButton = true,
		shouldHighlightCurlyBraces = false,
		colorRowOnHover = false,
		selectedRowsSet = new Set<any>(),
		showFilters = true,
		enableSorting = false,
		columnStyles = {},
		columnComponents = {},
		editableColumns = [],
		columnWidths = [],
		onSelectedRowsSetUpdate = null,
		contextMenuOptions = null,
		onDoubleClickRow = null,
		onsubmit = null,
		onRowClicked = null,
		onDeleteButtonClicked = null,
		onNewProjectButtonClicked = null,
		onActionHandler = null
	}: Props = $props()

	let contextMenuProps: ContextMenuProps | null = $state(null)

	columns = columns.filter((column: string) => !filteredOutColumns.includes(column))

	// --- ROW SELECT ---
	const toggleRowSelection = (id: any) => {
		const updatedSet = new Set(selectedRowsSet)
		if (updatedSet.has(id)) {
			updatedSet.delete(id)
		} else {
			updatedSet.add(id)
		}

		onSelectedRowsSetUpdate?.(updatedSet)
	}

	const selectAllRows = (checked: boolean) => {
		const updatedSet = checked ? new Set(rows.map((row) => row.id)) : new Set()
		onSelectedRowsSetUpdate?.(updatedSet)
	}

	const handleCheckboxChange = (checked: boolean, id: number) => {
		if (id === -1) {
			// Header checkbox was clicked
			selectAllRows(checked)
		} else {
			// Row checkbox was clicked
			toggleRowSelection(id)
		}
	}

	const handleContextMenu = (options: ContextMenuProps | null) => (contextMenuProps = options)

	function generateGridTemplate(columns: string[], includeCheckboxColumn: boolean = false): string {
		if (columns.length === 0) return '1fr'

		const template: string[] = []

		if (includeCheckboxColumn) {
			template.push('40px') // fixed width for selection checkbox
		}

		columns.forEach((_, i) => {
			if (i === columns.length - 1) {
				template.push('max-content')
			} else if (i === columns.length - 2 && columns.length >= 2) {
				template.push('1fr') // second to last becomes stretchable
			} else {
				template.push('max-content')
			}
		})

		return template.join(' ')
	}

	const onUpdateSorting = (updatedRows: any[]) => {
		rows = updatedRows
	}

	const gridStyle = $derived.by(() => {
		if (columnWidths && columnWidths.length > 0) {
			const widths = [...(showSelectionCheckboxes ? ['40px'] : []), ...columnWidths]
			return `grid-template-columns: ${widths.join(' ')};`
		}
		return `grid-template-columns: ${generateGridTemplate(columns, showSelectionCheckboxes)};`
	})
</script>

<svelte:head>
	<style>
		.sortable-header {
			cursor: pointer;
			display: flex;
			align-items: center;
		}
		.sort-icon {
			margin-left: 5px;
		}
	</style>
</svelte:head>

<div class="table-boxs">
	<div>
		<ProjectConfiguration
			{toastsStore}
			{showTableTopDeleteButton}
			{showTableTopNewProjectButton}
			{selectedRowsSet}
			{onDeleteButtonClicked}
			{onNewProjectButtonClicked}
		/>

		<div class="wrapper" style={gridStyle}>
			<TableHeader
				{columns}
				enableRowSelect={showSelectionCheckboxes}
				{selectedRowsSet}
				{columnStyles}
				{showFilters}
				{enableSorting}
				{rows}
				{onUpdateSorting}
				{handleCheckboxChange}
			/>

			<TableBody
				{rows}
				enableRowSelect={showSelectionCheckboxes}
				{selectedRowsSet}
				{columns}
				{columnsToSplit}
				{columnStyles}
				{editableColumns}
				{jsonColumns}
				{locale}
				{columnComponents}
				{shouldHighlightCurlyBraces}
				{colorRowOnHover}
				{handleContextMenu}
				{handleCheckboxChange}
				onRowDbClick={onDoubleClickRow}
				{onsubmit}
				{onActionHandler}
				{onRowClicked}
				{contextMenuOptions}
			/>
		</div>
	</div>

	<ContextMenuRenderer {contextMenuProps} />
</div>
<div class="pagination">
	<!-- <Pagination /> -->
</div>

<style lang="postcss">
	.wrapper {
		display: grid;

		background-color: #fff;
		color: #444;
	}

	.table-boxs {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
	.pagination {
		margin-top: 24px;
	}
</style>
